import {Controller} from "@hotwired/stimulus"
import Choices from 'choices.js';
import {useZoomListener} from "./mixins/use_zoom_listener.js";
import {useResizeListener} from "./mixins/use_resize_listener.js";
import {useLeftPanelChangeListener} from "./mixins/use_left_panel_change_listener.js";

/**
 * This controller uses Choices.js library to create a custom select element.
 * See https://github.com/Choices-js/Choices for more details.
 *
 * SelectSearchController allows us to:
 * 1. Search within the rendered list of <option> elements
 * 2. Refresh links on the page containing entity IDs
 *    by dispatching the event to EntityChangeController
 * 3. Unify the appearance of <select> elements across all browser types
 *    applying Choices.js library
 *
 * Basic usage:
 * %div{ data: {controller: 'select-search'} }
 *   %select{ data: {select_search_target: 'selectElement'} }
 *
 * Note: never use <select> element as a controlled element!
 *
 *
 * # Synchronizing links containing entity_id
 *
 * If your select changes `entity_id` and the page contains links
 * that use this parameter, when the select value changes,
 * these links must be synchronized so that
 * each link leads to the correct page.
 * You can ensure this by giving the select target `entitySource`
 * to selectElement:
 *   data: {select_search_target: 'selectElement entitySource'}
 *
 * This controller then sends the corresponding event
 * to EntityChangeController.
 *
 *
 * # Hidable parent
 *
 * If the select is inside a hideable parent,
 * this would lead to an incorrect calculation of the width
 * of the basic element of the Choices.js library
 * (see `_getDropdownWidth` method).
 * To avoid this, add the `parentMaybeHidden` target to selectElement:
 *  { data: {select_search_target: 'selectElement parentMaybeHidden'} }
 * and external target to hideable parent:
 *  { data: {external_select_search_target: 'selectMaybeHidden'} }
 *
 *
 * # Waiting for the <select> element content to load
 *
 * The content of <select> elements is sometimes loaded
 * dynamically when connecting the controller (e.g. using fetch).
 * In order for the Choices.js library to create
 * the correct structure of options, it is necessary
 * to wait for the content to load completely.
 * We ensure this by setting the controller value `waitForInitialFetch` to true:
 *  data: { controller: 'select-search', select_search_wait_for_initial_fetch_value: 'true' }
 *
 * In other cases, when the content of a <select> element depends
 * on the selected option of another <select> element, changing the option also
 * causes asynchronous content loading.
 * We can ensure that the content loading is completed
 * by setting the controller value waitForFutureFetch to true:
 *  data: { controller: 'select-search', select_search_wait_for_future_fetch_value: 'true' }
 *
 *
 * # Disable search
 *
 * Sometimes the content of the <select> element is so short
 * that it is unnecessary to use the search feature.
 * This feature is enabled by default. If we want to disable it,
 * we use the controller value `useSearch`:
 *  data: { controller: 'select-search', select_search_use_search_value: 'false' }
 */

export default class extends Controller {
  static targets = [
    'selectElement',
    'entitySource',
    'parentMaybeHidden',
  ]

  static values = {
    waitForInitialFetch: {type: Boolean, default: false},
    waitForFutureFetch: {type: Boolean, default: false},
    useSearch: {type: Boolean, default: true},
  }

  connect() {
    this._ensureThisElementIsNotSelect();
    super.connect();
    this._maybeWaitForFetch();

    this.previousValue = this.selectElementTarget.value;
    this.selectElementTarget.addEventListener('change', this.confirmChangeHandler ||= this.confirmChange.bind(this));

    this._setupFieldsetObserver();
    this.updateDisabledState();

    useZoomListener(this);
    useResizeListener(this);
    useLeftPanelChangeListener(this);
  }

  disconnect() {
    super.disconnect();
    this.selectElementTarget.removeEventListener('select:loaded', this.selectLoadedHandler);
    this.selectElementTarget.removeEventListener('showDropdown', this.showDropdownHandler);
    this.selectElementTarget.removeEventListener('change', this.confirmChangeHandler);

    if (this.choices) {
      this.choices.passedElement.element.removeEventListener('change', this.entityChangeHandler);
      this.choices.destroy();
      this.choices = null;
    }

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  confirmChange(event) {
    const message = this.element.dataset.confirmOnChange;
    if (!message) {
      this.previousValue = event.target.value;
      return;
    }

    if (!window.confirm(message)) {
      this._restorePreviousValue();
      return;
    }

    this.previousValue = event.target.value;
  }

  _restorePreviousValue() {
    const value = this.previousValue;

    this.selectElementTarget.value = value;

    if (this.choices) {
      this.choices.removeActiveItems();
      this.choices.setChoiceByValue(value);
    }
  }




  /**
   * Ensures that this element is not a <select> element -
   * this would lead to incorrect behavior of the Choices.js library.
   */
  _ensureThisElementIsNotSelect() {
    if (this.element.tagName && this.element.tagName.toLowerCase() === 'select') {
      console.error('SelectSearchController: this.element is <select> element. Please use <div data-controller="select-search"> instead.');
      throw new Error('SelectSearchController: this.element is <select> element. Please use <div data-controller="select-search"> instead.');
    }
  }

  _maybeWaitForFetch() {
    if (this.waitForInitialFetchValue) {
      this._appendLoadHandler();
      return;
    }

    this._selectLoaded();

    if (this.waitForFutureFetchValue) {
      this._appendLoadHandler();
    }
  }

  /**
   * Make sure that <select> element content is fully loaded
   * before applying the Choices.js library.
   * Use
   *    import dispatchSelectLoaded from './utils/dispatch_select_loaded'
   *    ...
   *      dispatchSelectLoaded(selectElement)
   * to dispatch the event in all places where
   * the <select> element content is loaded asynchronously,
   * e.g. using fetch.
   */
  _appendLoadHandler() {
    this.selectLoadedHandler = this._selectLoaded.bind(this);
    this.selectElementTarget.addEventListener('select:loaded', this.selectLoadedHandler);
  }

  _selectLoaded() {
    this._applyChoices();
    if (!this.choices) return;

    this.updateDisabledState();

    this.showDropdownHandler = this._adjustDropdownHeight.bind(this);
    this.selectElementTarget.addEventListener('showDropdown', this.showDropdownHandler);

    this._getDropdownWidth().then(width => {
      this.choices.containerOuter.element.style.minWidth = `${width}px`;
    });
  }

  _applyChoices() {
    /** Make sure that the Choices.js library is initialized only once. */
    this.choices && this.choices.destroy();

    if (!this.hasSelectElementTarget) return;

    this.choices = new Choices(this.selectElementTarget, {
      shouldSort: false,
      searchEnabled: this.useSearchValue,
      searchPlaceholderValue: window.I18n.t('choices.search'),
      noResultsText: window.I18n.t('choices.no_results'),
      searchResultLimit: -1,
      silent: false,
      itemSelectText: '',
      searchFields: ['label'],
      /**
       * Set fuseOptions to customize search behavior.
       * see https://fusejs.io/api/options.html for details.
       */
      fuseOptions: {
        getFn: (obj, path) => {
          const original = obj.label || "";
          const normalized = this._normalizeString(original);
          return [original, normalized];
        },
        threshold: 0.2,
        location: 0,
        distance: 100,
        minMatchCharLength: 1,
        findAllMatches: true,
        ignoreLocation: true,
      }
    });

    this._maybeSyncEntity();
  }

  /**
   * Normalize the string so that
   * diacritic-independent search can be used.
   */
  _normalizeString(str) {
    return str ? str.normalize('NFD').replace(/[\u0300-\u036f]/g, "").toLowerCase() : "";
  }

  _setInitiallySelectedEntityId() {
    return this.choices.getValue(true);
  }

  /**
   * Determines if entity synchronization is active
   * based on the presence of an entity source target
   */
  _isEntitySyncActive() {
    return this.hasEntitySourceTarget;
  }

  _maybeSyncEntity() {
    if (!this._isEntitySyncActive()) return;

    this._sendInitialEntityViaEvent();
    this._addEntityChangeHandler();
  }

  _sendInitialEntityViaEvent() {
    const entityId = this._setInitiallySelectedEntityId();
    this._dispatchEntityChangedEvent(entityId);
  }

  _addEntityChangeHandler() {
    this.entityChangeHandler = this._handleEntityChange.bind(this);
    this.choices.passedElement.element.addEventListener(
      'change',
      this.entityChangeHandler,
      true,
    );
  }

  _handleEntityChange(e) {
    this._dispatchEntityChangedEvent(e.target.value)
  }

  _dispatchEntityChangedEvent(entityId) {
    const event = new CustomEvent('select-search:entity_selected', {
      detail: {entityId: entityId},
      bubbles: true}
    );
    this.element.dispatchEvent(event);
  }

  /**
   * By default, Choices.js sets the width of the main element
   * dynamically according to the currently selected option.
   * However, we want the width of the main element
   * to match the width of the dropdown.
   */
  _getDropdownWidth() {
    if (!this.choices) return Promise.resolve(0);

    return new Promise(resolve => {
      this.choices.dropdown.element.classList.add('invisible');
      this.choices.showDropdown();
      let maybeHiddenParent;
      let maybeHiddenParentDisplayStyle;

      if (this.hasParentMaybeHiddenTarget) {
        maybeHiddenParent = this.element.closest('[data-external-select-search-target="selectMaybeHidden"]');
        if (maybeHiddenParent) {
          maybeHiddenParentDisplayStyle = getComputedStyle(maybeHiddenParent).display;
          maybeHiddenParent.classList.add('invisible');
          maybeHiddenParent.style.removeProperty('display');
        }
      }

      requestAnimationFrame(() => {
        resolve(this.choices.dropdown.element.getBoundingClientRect().width);
        this.choices.hideDropdown();

        requestAnimationFrame(() => {
          this.choices.dropdown.element.classList.remove('invisible');
          if (maybeHiddenParent) {
            maybeHiddenParent.classList.remove('invisible');
            maybeHiddenParent.style.display = maybeHiddenParentDisplayStyle;
          }
        });
      });
    });
  }

  _isDropdownFlipped() {
    return this.choices.containerOuter.element.classList.contains('is-flipped');
  }

  /**
   * Adjusts the height of the dropdown
   * so that it does not exceed the viewport.
   * It overrides the default css value 300 px.
   */
  _adjustDropdownHeight() {
    /** Skip if dropdown is measured by _getDropdownWidth */
    if (this.choices.dropdown.element.classList.contains('invisible')) return;

    const dropdownEl = this.choices.dropdown.element;
    const containerRect = this.choices.containerOuter.element.getBoundingClientRect();
    const inputHeight = this.choices.input.element.getBoundingClientRect().height;
    const minDistance = 10;
    let maxHeight;

    if (this._isDropdownFlipped()) {
      maxHeight = containerRect.top - minDistance - inputHeight;
    } else {
      maxHeight = window.innerHeight - containerRect.bottom - minDistance - inputHeight;
    }

    const listElement = dropdownEl.querySelector('.choices__list');
    if (listElement) {
      listElement.style.maxHeight = `${maxHeight}px`;
    }
  }

  onResizeChange() {
    this._selectLoaded();
  }

  onLeftPanelChange() {
    this._selectLoaded();
  }

  onZoomChange() {
    this._selectLoaded();
  }

  isInsideDisabledFieldset() {
    return this.element.closest('fieldset:disabled') !== null;
  }

  updateDisabledState() {
    const isDisabled = this.isInsideDisabledFieldset();

    if (this.hasSelectElementTarget) {
      this.selectElementTarget.disabled = isDisabled;
    }

    if (this.choices) {
      if (isDisabled) {
        this.choices.disable();
      } else {
        this.choices.enable();
      }
    }

    this.element.classList.toggle('is-disabled', isDisabled);
  }

  _setupFieldsetObserver() {
    this.observer = new MutationObserver(() => {
      this.updateDisabledState();
    });

    let parent = this.element.parentElement;

    while (parent) {
      if (parent.tagName === 'FIELDSET') {
        this.observer.observe(parent, {
          attributes: true,
          attributeFilter: ['disabled']
        });
      }
      parent = parent.parentElement;
    }
  }
}